// useIntents.jsx — the signed-in user's intents (see utils/intents.js), shared
// app-wide. One users-doc read per sign-in.
//
// Signed-out visitors get the intent from an ad link's ?for= instead, kept for
// the session so it survives navigating around and carries into signup.
//
// OnboardingGate sends a verified user who has never answered (no
// intentSetAt) to /welcome, once per session.
import React, { createContext, useContext, useEffect, useState } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { useLocation, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { AD_PARAM } from "../utils/intents";

const AD_KEY = "jale:for";

// Read ?for= once at load and remember it for this session.
function captureAdIntent() {
  try {
    const raw = new URLSearchParams(window.location.search).get("for");
    const key = raw && AD_PARAM[raw.toLowerCase()];
    if (key) sessionStorage.setItem(AD_KEY, key);
    return sessionStorage.getItem(AD_KEY) || null;
  } catch {
    return null;
  }
}

const IntentsContext = createContext({
  uid: null,
  loaded: false,
  intents: [],
  answered: false,
  adIntent: null,
  primary: null,
  setLocal: () => {},
});

export function IntentsProvider({ children }) {
  const [adIntent] = useState(captureAdIntent);
  const [state, setState] = useState({ uid: null, loaded: false, intents: [], answered: false });

  useEffect(() => {
    return onAuthStateChanged(getAuth(), async (user) => {
      if (!user) {
        setState({ uid: null, loaded: true, intents: [], answered: false });
        return;
      }
      try {
        const d = (await getDoc(doc(db, "users", user.uid))).data() || {};
        setState({
          uid: user.uid,
          loaded: true,
          intents: Array.isArray(d.intents) ? d.intents : [],
          answered: !!d.intentSetAt,
        });
      } catch {
        // Can't tell — don't nag.
        setState({ uid: user.uid, loaded: true, intents: [], answered: true });
      }
    });
  }, []);

  // After saving on /welcome, update without a re-read.
  const setLocal = (intents) => setState((s) => ({ ...s, intents, answered: true }));

  const intents = state.uid ? state.intents : adIntent ? [adIntent] : [];
  const value = { ...state, intents, adIntent, primary: intents[0] || null, setLocal };
  return <IntentsContext.Provider value={value}>{children}</IntentsContext.Provider>;
}

export function useIntents() {
  return useContext(IntentsContext);
}

// Pages where we never interrupt: auth flow, the welcome screen itself, legal,
// and the standalone portfolio.
const NO_GATE = [/^\/welcome/, /^\/verify-email/, /^\/create-username/, /^\/sign-/, /^\/forgot-password/, /^\/terms/, /^\/privacy/, /^\/yard-office/, /^\/portfolio/, /^\/adams-projects/];
let gatedThisSession = false;

export function OnboardingGate() {
  const { uid, loaded, answered } = useIntents();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    // Read verification live: VerifyEmail reloads the auth user in place, so
    // this is current right after someone clicks their link — the flag
    // captured at sign-in would still say false.
    const verified = getAuth().currentUser?.emailVerified;
    if (!loaded || !uid || !verified || answered || gatedThisSession) return;
    if (NO_GATE.some((re) => re.test(pathname))) return;
    gatedThisSession = true;
    navigate("/welcome", { replace: true });
  }, [loaded, uid, answered, pathname, navigate]);
  return null;
}
